import { useState } from "react";
import { IconLock } from "@tabler/icons-react";
import { Badge, EditRowActions, Group, Input, Switch, Td, Text, Tr, useConfirmDialog } from "@/components/ui";
import type { PermisoGestion } from "../services/permisos-service";
import { useCambiarEstadoPermiso, useEditarPermiso, useEliminarPermiso } from "../hooks/use-permisos-mutations";

/**
 * Fila de un permiso del catálogo. Vista/edición inline de METADATOS
 * (grupo + descripción · el código es inmutable). Switch de activo. Los
 * permisos de SISTEMA no se eliminan (solo desactivar); los personalizados sí.
 */
export function PermisoRow({ permiso }: { permiso: PermisoGestion }) {
  const [editando, setEditando] = useState(false);
  const [grupo, setGrupo] = useState(permiso.grupo);
  const [descripcion, setDescripcion] = useState(permiso.descripcion);
  const confirm = useConfirmDialog();

  const guardar = useEditarPermiso({ onEditado: () => setEditando(false) });
  const estado = useCambiarEstadoPermiso();
  const eliminar = useEliminarPermiso();

  const cancelar = () => {
    setGrupo(permiso.grupo);
    setDescripcion(permiso.descripcion);
    setEditando(false);
  };

  const confirmarEliminar = async () => {
    const ok = await confirm({
      title: "Eliminar permiso",
      message: (
        <>
          ¿Eliminar el permiso <b>{permiso.codigo}</b>? Solo se pueden eliminar permisos personalizados
          sin roles asignados.
        </>
      ),
      confirmLabel: "Eliminar",
      confirmColor: "peligro",
    });
    if (ok) eliminar.mutate(permiso.id);
  };

  if (!editando) {
    return (
      <Tr>
        <Td>
          <Group gap={6} align="center">
            {permiso.esDeSistema ? <IconLock size={13} className="text-muted-foreground" /> : null}
            <Text size="sm" fw={500} className="font-mono">
              {permiso.codigo}
            </Text>
          </Group>
        </Td>
        <Td>
          <Text size="sm">{permiso.descripcion}</Text>
        </Td>
        <Td>
          <Group gap={6}>
            <Badge size="sm" color={permiso.esDeSistema ? "info" : "slate"}>
              {permiso.esDeSistema ? "Sistema" : "Personalizado"}
            </Badge>
            {permiso.pendienteCablear ? (
              <Badge size="sm" color="advertencia" title="No otorga acceso hasta cablearse en el código">
                Pendiente de cablear
              </Badge>
            ) : null}
          </Group>
        </Td>
        <Td align="center">
          <Text size="sm" className="tabular-nums">
            {permiso.rolesCount}
          </Text>
        </Td>
        <Td>
          <Switch
            checked={permiso.activo}
            onCheckedChange={(v) => estado.mutate({ id: permiso.id, activo: v })}
            label={permiso.activo ? "Activo" : "Inactivo"}
            disabled={estado.isPending}
          />
        </Td>
        <Td align="right">
          <EditRowActions
            mode="view"
            onEdit={() => setEditando(true)}
            onDelete={permiso.esDeSistema ? undefined : confirmarEliminar}
            deleting={eliminar.isPending}
          />
        </Td>
      </Tr>
    );
  }

  return (
    <Tr className="bg-muted/50">
      <Td>
        <Text size="sm" fw={500} className="font-mono">
          {permiso.codigo}
        </Text>
      </Td>
      <Td>
        <Input size="xs" value={descripcion} onChange={(e) => setDescripcion(e.currentTarget.value)} maxLength={300} autoFocus />
      </Td>
      <Td>
        <Input size="xs" value={grupo} onChange={(e) => setGrupo(e.currentTarget.value)} maxLength={60} placeholder="Grupo" className="w-40" />
      </Td>
      <Td align="center">
        <Text size="sm" className="tabular-nums">
          {permiso.rolesCount}
        </Text>
      </Td>
      <Td>
        <Badge size="sm" color={permiso.activo ? "exito" : "slate"}>
          {permiso.activo ? "Activo" : "Inactivo"}
        </Badge>
      </Td>
      <Td align="right">
        <EditRowActions
          mode="edit"
          saving={guardar.isPending}
          onCancel={cancelar}
          onSave={() => guardar.mutate({ id: permiso.id, input: { grupo: grupo.trim(), descripcion: descripcion.trim() } })}
        />
      </Td>
    </Tr>
  );
}
